// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { MyAccountLinkModel } from "@eisinc/App/Store/Selectors/HeaderSelector";
import { getPageLinkByPageType } from "@eisinc/App/Store/Selectors/LinksSelectors";
import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { color, mergeWithCss } from "@eisinc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface MiniSignInProps {
    isPunchOutSession?: boolean;
    isAllowedToPaymentCenter?: boolean;
    onSignOutHandler?: () => void;
    links?: MyAccountLinkModel[];
    extendedStyles?: MiniMyAccountStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        billToState: getBillToState(state, getSession(state).billToId)?.value,
        myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
        isEisPaymentCenterRole: getSession(state).userRoles?.includes("EisPaymentCenter"),
    };
};

type Props = MiniSignInProps & ReturnType<typeof mapStateToProps>;

export interface MiniMyAccountStyles {
    container?: InjectableCss;
    topContainer?: InjectableCss;
    headingContainer?: InjectableCss;
    headingLink?: LinkPresentationProps;
    linksContainer?: InjectableCss;
    groupHeading?: TypographyPresentationProps;
    itemLinkContainer?: InjectableCss;
    itemLink?: LinkPresentationProps;
    billToContainer?: InjectableCss;
    billToHeading?: TypographyPresentationProps;
    billToInfoSection?: InjectableCss;
    billingCompany?: TypographyPresentationProps;
    billingAccount?: TypographyPresentationProps;
    billToImageSection?: InjectableCss;
    billingImage?: LazyImageProps;
    buttonContainer?: InjectableCss;
    signOutButton?: ButtonPresentationProps;
}

export const miniMyAccountStyles: MiniMyAccountStyles = {
    container: {
        css: css`
            width: 430px;
            min-width: 300px;
        `,
    },
    topContainer: {
        css: css`
            padding: 12px;
        `,
    },
    headingContainer: {
        css: css`
            width: 100%;
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 12px;
            margin-bottom: 12px;
        `,
    },
    headingLink: {
        color: "text.main",
        typographyProps: {
            size: fontConfig.singleLineLarge.size,
            lineHeight: fontConfig.singleLineLarge.lineHeight,
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    linksContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12px 24px;
            margin-bottom: 16px;
        `,
    },
    groupHeading: {
        size: fontConfig.singleLineSmall.size,
        lineHeight: fontConfig.singleLineSmall.lineHeight,
        weight: fontConfig.weight.medium,
        css: css`
            width: 100%;
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 12px;
            margin-bottom: 12px;
        `,
    },
    itemLink: {
        typographyProps: {
            size: fontConfig.singleLinexSmall.size,
            lineHeight: fontConfig.singleLinexSmall.lineHeight,
        },
        css: css`
            align-self: baseline;
        `,
    },
    billToContainer: {
        css: css`
            display: grid;
            grid-template-columns: 1fr 130px;
            gap: 12px 24px;
        `,
    },
    billToHeading: {
        size: fontConfig.singleLineSmall.size,
        lineHeight: fontConfig.singleLineSmall.lineHeight,
        weight: fontConfig.weight.medium,
        css: css`
            border-bottom: 1px solid ${color("common.border")};
            padding-bottom: 8px;
            margin-bottom: 0;
            grid-column: 1/3;
        `,
    },
    billToInfoSection: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
        `,
    },
    billingCompany: {
        size: fontConfig.singleLinexSmall.size,
        lineHeight: fontConfig.singleLinexSmall.lineHeight,
        weight: fontConfig.weight.medium,
    },
    billingAccount: {
        size: fontConfig.singleLinexSmall.size,
        lineHeight: fontConfig.singleLinexSmall.lineHeight,
        weight: fontConfig.weight.regular,
    },
    billingImage: {
        width: "140px",
        height: "60px",
        css: css`
            max-width: 140px;
            max-height: 60px;
            img {
                max-width: 100%;
                max-height: 100%;
                width: auto;
                height: auto;
            }
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            justify-content: center;
            background: ${color("common.ui3")};
            margin-top: 20px;
            padding: 12px;
        `,
    },
    signOutButton: {
        variant: "primary",
    },
};

const MiniMyAccount: FC<Props> = ({
    billToState,
    myAccountPageUrl,
    onSignOutHandler,
    isPunchOutSession,
    isAllowedToPaymentCenter,
    isEisPaymentCenterRole,
    links,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.container} data-test-selector="header-mini-myAccount">
            <StyledWrapper {...styles.topContainer}>
                <StyledWrapper {...styles.headingContainer}>
                    <Link {...styles.headingLink} href={myAccountPageUrl}>
                        {translate("My Account")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.linksContainer}>
                    {links?.map((link, idx) => {
                        if (
                            (link.pageType === "PaymentCenterPage" && !isAllowedToPaymentCenter) ||
                            (["PaymentCenterPage", "InvoicePaymentHistoryPage"].includes(link.pageType || "") &&
                                !isEisPaymentCenterRole)
                        ) {
                            return;
                        }

                        return link?.isGroup ? (
                            <Typography
                                as="div"
                                key={`${link.title}_${idx}_group`}
                                {...styles.headingLink?.typographyProps}
                            >
                                {translate(link.title)}
                            </Typography>
                        ) : (
                            <StyledWrapper key={`${link.url}_${idx}_wrapper`} {...styles.itemLinkContainer}>
                                <Link key={`${link.url}_${idx}`} href={link.url} {...styles.itemLink}>
                                    {link.title}
                                </Link>
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
                <StyledWrapper {...styles.billToContainer}>
                    <Typography {...styles.billToHeading}>{translate("Logged in as")}:</Typography>
                    <StyledWrapper {...styles.billToInfoSection}>
                        <Typography {...styles.billingCompany}>{billToState?.companyName}</Typography>
                        <Typography {...styles.billingAccount}>
                            {translate("Account #")}: {billToState?.customerNumber}
                        </Typography>
                    </StyledWrapper>
                    {billToState?.properties["logoImagePath"] && (
                        <StyledWrapper {...styles.billToImageSection}>
                            <LazyImage
                                src={billToState?.properties["logoImagePath"]}
                                altText={billToState?.companyName}
                                {...styles.billingImage}
                            />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.buttonContainer}>
                <Button {...styles.signOutButton} onClick={onSignOutHandler} data-test-selector="headerSignOutLink">
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(MiniMyAccount);
